<script setup lang="ts">
import EventCheckpointAutocomplete from '@/components/Filters/components/EventCheckpointAutocomplete.vue'
import { GridLayout } from '@/components/Layouts'
import DeviceFilterAutocomplete from '@/components/Selector/components/DeviceFilterAutocomplete.vue'
import { useGridSize } from '@/hooks/useGridSize'
import type { DetectionFilters } from '@/types/filters/DetectionFilters'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { DetectionModel } from '@/types/models/DetectionModel'
import type { EventModel } from '@/types/models/EventModel'
import { inject } from 'vue'
import DebouncedInputField from '../../components/Fields/DebouncedInputField.vue'
import DebouncedNumberInputField from '../../components/Fields/DebouncedNumberInputField.vue'
import { EHubContext } from '../../types/context/HubContextEnum'

type Props = {
    filters: DetectionFilters
    selectedEvent?: Partial<EventModel>
    selectedCheckpoint?: Partial<CheckpointModel>
}
defineProps<Props>()

type Emits = {
    (e: 'change', value: Partial<DetectionModel>): void
    (e: 'selectEvent', value?: EventModel): void
    (e: 'selectCheckpoint', value?: CheckpointModel): void
}
defineEmits<Emits>()

const gridSize = useGridSize()

const onWheel = () => {}

const isViewerOnly = inject<boolean>(EHubContext.IS_VIEWER_ONLY, false)
</script>

<template>
    <GridLayout
        :maxItemPerRow="gridSize"
        :gap="8"
        :rowGap="12"
        class="full-width flex-wrap"
        isResponsive>
        <EventCheckpointAutocomplete
            :event="selectedEvent"
            :checkpoint="selectedCheckpoint"
            keepEventInStorage
            filterAllEvents
            @selectEvent="$emit('selectEvent', $event)"
            @selectCheckpoint="$emit('selectCheckpoint', $event)" />
        <DeviceFilterAutocomplete
            :filters="filters"
            :deviceOptions="selectedCheckpoint?.associates ?? selectedEvent?.detection_devices"
            :disabled="isViewerOnly && !filters.checkpoint_id"
            @change="$emit('change', $event)" />
        <DebouncedNumberInputField
            :label="$t('detection.filters.eventId')"
            :modelValue="filters.event_filter"
            @change="$emit('change', { event_filter: $event })"
            :prefix="$t('unit.number')"
            :min="1"
            @wheel="onWheel" />
        <DebouncedNumberInputField
            :label="$t('detection.filters.timerId')"
            :modelValue="filters.timer_filter"
            :prefix="$t('unit.number')"
            :min="1"
            @change="$emit('change', { timer_filter: $event })"
            @wheel="onWheel" />

        <DebouncedInputField
            :label="$t('detection.filters.bib')"
            :modelValue="filters.bib"
            @change="$emit('change', { bib: $event })" />
    </GridLayout>
</template>
